import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { get } from '../../lib/utils/axiosFetcher';

/**
 * API Response from LMA/GetLMAFilterDropdownValues
 */
export interface LMADropdownResponse {
  cities?: string[];
  states?: string[];
  ethnicity?: string[];
  associatedProtocols?: Array<{
    protocol: string;
    sponsorName: string;
    studyName: string;
    nctid: string;
    sites: Array<{
      code: string;
      name: string;
      city: string;
      state: string;
    }>;
  }>;
  qualifiedProtocols?: string[];
  lmaUsers?: Array<{
    userId: string;
    userName: string;
  }>;
}

export interface DropdownState {
  data: LMADropdownResponse | null;
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
}

const initialState: DropdownState = {
  data: null,
  loading: false,
  error: null,
  lastUpdated: null,
};

// Async thunk for fetching dropdown data (called once on page load)
export const fetchDropdownData = createAsyncThunk(
  'dropdown/fetchDropdownData',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await get('/LMA/GetLMAFilterDropdownValues');
      return response?.data as LMADropdownResponse;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Failed to fetch dropdown data';
      dispatch({
        type: 'toast/showToast',
        payload: { message: errorMessage, severity: 'error' },
      });
      return rejectWithValue(errorMessage);
    }
  }
);

const dropdownSlice = createSlice({
  name: 'dropdown',
  initialState,
  reducers: {
    clearDropdownData: (state) => {
      state.data = null;
      state.error = null;
      state.lastUpdated = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDropdownData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDropdownData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.lastUpdated = new Date().toISOString();
        console.log('[Dropdown Slice] Data loaded successfully:', action.payload);
      })
      .addCase(fetchDropdownData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        console.error('[Dropdown Slice] Error loading data:', action.payload);
      });
  },
});

export const { clearDropdownData } = dropdownSlice.actions;
export default dropdownSlice.reducer;
